'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard, Package, Layers, KanbanSquare, Store, FileText,
  ChevronLeft, ChevronRight, Settings, LogOut, Users, Tag, Megaphone,
  UserCheck, Truck, Car, CheckSquare, RotateCcw, Bell, BarChart2,
  CreditCard, ShoppingCart, ChevronDown, ChevronUp, Globe,
  Shield, BarChart, Barcode, MessageCircle, FileBarChart, Key, Coffee
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslations, SUPPORTED_LANGUAGES } from '@/lib/i18n';
import { useRole, ROLE_LABELS } from '@/contexts/RoleContext';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
}

interface NavSection {
  key: string;
  labelKey: string;
  items: NavItem[];
  roles: ('supplier' | 'retailer' | 'hybrid')[];
}

interface SidebarProps {
  role?: 'supplier' | 'retailer' | 'hybrid';
}

export default function Sidebar({ role = 'hybrid' }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    core: true,
    sales: true,
    logistics: false,
    finance: false,
    purchases: false,
    retailer: true,
    roles_tools: false,
  });
  const pathname = usePathname();
  const { language, setLanguage, dir } = useLanguage();
  const { activeStaff, logoutRole, getRoleColor } = useRole();
  const t = getTranslations(language);

  const navSections: NavSection[] = [
    {
      key: 'core',
      labelKey: 'section.supplier',
      roles: ['supplier', 'hybrid'],
      items: [
        { label: t('nav.dashboard'), href: '/', icon: LayoutDashboard },
        { label: t('nav.catalog'), href: '/product-catalog-grid-view', icon: Package, badge: 3 },
        { label: t('nav.fifo'), href: '/fifo-batch-inventory-manager', icon: Layers, badge: 5 },
        { label: t('nav.kanban'), href: '/order-fulfillment-kanban-board', icon: KanbanSquare, badge: 7 },
      ],
    },
    {
      key: 'sales',
      labelKey: 'section.sales',
      roles: ['supplier', 'hybrid'],
      items: [
        { label: t('nav.crm'), href: '/crm-retailer-profiles', icon: Users },
        { label: t('nav.pricelists'), href: '/price-lists', icon: Tag },
        { label: t('nav.promotions'), href: '/promotional-campaigns', icon: Megaphone },
        { label: t('nav.salesreps'), href: '/sales-reps', icon: UserCheck },
      ],
    },
    {
      key: 'logistics',
      labelKey: 'section.logistics',
      roles: ['supplier', 'hybrid'],
      items: [
        { label: t('nav.delivery'), href: '/delivery-scheduling', icon: Truck },
        { label: t('nav.fleet'), href: '/fleet-management', icon: Car },
        { label: t('nav.confirmation'), href: '/delivery-confirmation', icon: CheckSquare },
        { label: t('nav.returns'), href: '/returns-management', icon: RotateCcw },
      ],
    },
    {
      key: 'finance',
      labelKey: 'section.finance',
      roles: ['supplier', 'hybrid'],
      items: [
        { label: t('nav.debt'), href: '/debt-collection', icon: Bell },
        { label: t('nav.profitability'), href: '/profitability-reports', icon: BarChart2 },
        { label: t('nav.cheques'), href: '/cheque-settlement', icon: CreditCard },
        { label: language === 'ar' ? 'لوحة KPI' : 'Tableau KPI', href: '/kpi-dashboard', icon: BarChart },
        { label: language === 'ar' ? 'تقرير TVA' : 'Rapport TVA', href: '/tva-report', icon: FileBarChart },
      ],
    },
    {
      key: 'purchases',
      labelKey: 'section.purchases',
      roles: ['supplier', 'hybrid'],
      items: [
        { label: t('nav.purchases'), href: '/purchase-orders', icon: ShoppingCart },
      ],
    },
    {
      key: 'roles_tools',
      labelKey: 'section.roles_tools',
      roles: ['supplier', 'hybrid'],
      items: [
        { label: language === 'ar' ? 'الأدوار والصلاحيات' : 'Rôles & Permissions', href: '/supplier-roles', icon: Shield },
        { label: language === 'ar' ? 'طباعة الباركود' : 'Codes-Barres', href: '/barcode-print', icon: Barcode },
        { label: language === 'ar' ? 'إشعارات WhatsApp' : 'WhatsApp', href: '/whatsapp-notifications', icon: MessageCircle },
      ],
    },
    {
      key: 'retailer',
      labelKey: 'section.retailer',
      roles: ['retailer', 'hybrid'],
      items: [
        { label: t('nav.marketplace'), href: '/multi-supplier-marketplace-view', icon: Store },
        { label: t('nav.order'), href: '/direct-order-sheet', icon: FileText },
        { label: language === 'ar' ? '🏪 حانوت / بقالة' : '🏪 Épicerie / Hanout', href: '/retailer', icon: Store },
        { label: language === 'ar' ? '☕ مقهى / مطعم' : '☕ Café / Restaurant', href: '/cafe-restaurant', icon: Coffee },
      ],
    },
  ];

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const visibleSections = navSections.filter(s =>
    s.roles.includes(role) || (role === 'hybrid')
  );

  return (
    <aside
      className="sidebar-transition flex-shrink-0 bg-card border-r border-border flex flex-col h-screen sticky top-0 z-30"
      style={{ width: collapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)' }}
      dir={dir}
    >
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-4 py-4 border-b border-border min-h-[var(--topbar-height)]">
        <AppLogo size={32} />
        {!collapsed && (
          <div className="overflow-hidden">
            <span className="font-extrabold text-base text-foreground tracking-tight block">GerantLink</span>
            <span className="text-2xs text-muted-foreground block">gerant.ma</span>
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`${collapsed ? 'mx-auto' : 'ml-auto'} w-6 h-6 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground flex-shrink-0`}
        >
          {dir === 'rtl'
            ? (collapsed ? <ChevronLeft size={14} /> : <ChevronRight size={14} />)
            : (collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />)
          }
        </button>
      </div>

      {/* Active staff role badge */}
      {!collapsed && activeStaff && (
        <div className="px-4 pt-3 pb-1">
          <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${getRoleColor(activeStaff.roleType)}`}>
            <Key size={11} />
            <div className="flex-1 min-w-0">
              <p className="text-2xs font-700 truncate">{activeStaff.staffName}</p>
              <p className="text-2xs opacity-70">{ROLE_LABELS[activeStaff.roleType]?.ar}</p>
            </div>
          </div>
        </div>
      )}

      {/* Role badge (when no staff logged in) */}
      {!collapsed && !activeStaff && (
        <div className="px-4 pt-3 pb-1">
          <span className={`inline-flex items-center gap-1 text-2xs font-semibold px-2 py-0.5 rounded-full ${
            role === 'hybrid' ? 'status-badge-hybrid' :
            role === 'supplier' ? 'status-badge-loading' : 'status-badge-active'
          }`}>
            {role === 'hybrid' ? t('role.hybrid') : role === 'supplier' ? t('role.supplier') : t('role.retailer')}
          </span>
        </div>
      )}

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-2 py-3 space-y-0.5">
        {visibleSections.map((section) => (
          <div key={section.key} className="mb-1">
            {!collapsed && (
              <button
                onClick={() => toggleSection(section.key)}
                className="w-full flex items-center justify-between px-2 py-1.5 mt-1 group"
              >
                <p className="section-header text-2xs font-700 uppercase tracking-wider text-muted-foreground">
                  {t(section.labelKey)}
                </p>
                {expandedSections[section.key]
                  ? <ChevronUp size={12} className="text-muted-foreground" />
                  : <ChevronDown size={12} className="text-muted-foreground" />
                }
              </button>
            )}
            {(collapsed || expandedSections[section.key]) && section.items.map((item) => {
              const ItemIcon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={`nav-${section.key}-${item.href}`}
                  href={item.href}
                  title={collapsed ? item.label : undefined}
                  className={`flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 relative group ${
                    active ? 'nav-item-active' : 'nav-item-base'
                  }`}
                >
                  <ItemIcon size={17} className="flex-shrink-0" />
                  {!collapsed && <span className="flex-1 truncate text-xs">{item.label}</span>}
                  {!collapsed && item.badge && (
                    <span className="bg-danger text-white text-2xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                      {item.badge}
                    </span>
                  )}
                  {collapsed && item.badge && (
                    <span className="absolute top-1 right-1 bg-danger text-white text-2xs font-bold w-4 h-4 rounded-full flex items-center justify-center">
                      {item.badge}
                    </span>
                  )}
                  {collapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-foreground text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                      {item.label}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Language switcher */}
      {!collapsed && (
        <div className="px-3 py-2 border-t border-border">
          <div className="flex items-center gap-1.5 bg-muted rounded-lg p-1">
            <Globe size={13} className="text-muted-foreground mx-1 flex-shrink-0" />
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => setLanguage(lang.code)}
                className={`flex-1 py-1 rounded-md text-xs font-600 transition-colors ${
                  language === lang.code
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bottom actions */}
      <div className="border-t border-border p-2 space-y-0.5">
        <Link
          href="/supplier-roles"
          className="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-sm nav-item-base relative group"
          title={collapsed ? (language === 'ar' ? 'دخول PIN' : 'Accès PIN') : undefined}
        >
          <Key size={18} className="flex-shrink-0" />
          {!collapsed && <span className="text-xs">{language === 'ar' ? 'دخول PIN' : 'Accès PIN'}</span>}
          {collapsed && (
            <div className="absolute left-full ml-2 px-2 py-1 bg-foreground text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
              {language === 'ar' ? 'دخول PIN' : 'Accès PIN'}
            </div>
          )}
        </Link>
        <button
          className="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-sm nav-item-base relative group"
          title={collapsed ? t('nav.settings') : undefined}
        >
          <Settings size={18} className="flex-shrink-0" />
          {!collapsed && <span className="text-xs">{t('nav.settings')}</span>}
          {collapsed && (
            <div className="absolute left-full ml-2 px-2 py-1 bg-foreground text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
              {t('nav.settings')}
            </div>
          )}
        </button>
        {activeStaff ? (
          <button
            onClick={logoutRole}
            className="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-sm text-danger hover:bg-danger/10 transition-colors relative group"
            title={collapsed ? (language === 'ar' ? 'خروج الدور' : 'Quitter le rôle') : undefined}
          >
            <LogOut size={18} className="flex-shrink-0" />
            {!collapsed && <span className="text-xs">{language === 'ar' ? 'خروج الدور' : 'Quitter le rôle'}</span>}
          </button>
        ) : (
          <button
            className="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-sm text-danger hover:bg-danger/10 transition-colors relative group"
            title={collapsed ? t('nav.logout') : undefined}
          >
            <LogOut size={18} className="flex-shrink-0" />
            {!collapsed && <span className="text-xs">{t('nav.logout')}</span>}
            {collapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-foreground text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                {t('nav.logout')}
              </div>
            )}
          </button>
        )}
      </div>
    </aside>
  );
}